import { useEffect, useEffectEvent, useState } from 'react';

/**
 * State + dispatch pair for an ordered, id-keyed registry. `register`
 * returns its own unregister, and entries are exposed as a list rather
 * than a `Record`, suited to insertion-ordered scopes like dashboard tile
 * exports and per-message dashboard exports.
 *
 * Re-registering the same `id` replaces the prior entry in place — useful
 * when a producer's data identity changes and the registered closure
 * needs to be refreshed without churn in the list.
 */
export interface KeyedRegistry<T extends { readonly id: string }> {
  readonly entries: readonly T[];
  readonly register: (entry: T) => () => void;
}

export function useKeyedRegistry<T extends { readonly id: string }>(): KeyedRegistry<T> {
  const [entries, setEntries] = useState<readonly T[]>([]);
  const register = (entry: T) => {
    setEntries((prev) => [...prev.filter((e) => e.id !== entry.id), entry]);
    return () => setEntries((prev) => prev.filter((e) => e.id !== entry.id));
  };
  return { entries, register };
}

/**
 * Register `entry` with `registry` for as long as the calling component is
 * mounted. Both args may be `null` — the hook stays in the render path
 * (rules of hooks) but does nothing. Re-registers when `entry.id` changes;
 * any closure fields inside `entry` must already be stabilised by the
 * caller (`useEffectEvent`) so they resolve to latest values at call time
 * without re-registration churn.
 */
export function useRegisterInRegistry<T extends { readonly id: string }>(
  registry: KeyedRegistry<T> | null,
  entry: T | null,
): void {
  const registerLatest = useEffectEvent(() =>
    registry === null || entry === null ? undefined : registry.register(entry),
  );
  const id = entry?.id;
  useEffect(() => {
    if (id === undefined) {
      return;
    }
    return registerLatest();
  }, [id]);
}
/**
 * Register an `{ id, name }`-keyed entry whose only volatile field is a
 * single getter closure, for as long as the calling component is mounted.
 *
 * The getter is shielded through `useEffectEvent` so the registered entry
 * resolves to the latest closure at call time without re-registering every
 * time the producer's data re-renders. Pass `null` for `entry` to stay in
 * the render path (rules of hooks) while registering nothing.
 *
 * `getterKey` names the getter field on the registry entry and `getValue`
 * extracts the volatile value from a non-null entry; the getter's precise
 * return type `ValueType` is preserved on the registered entry.
 */
export function useRegisterWithGetter<
  EntryType extends { readonly id: string; readonly name: string },
  GetterKeyType extends string,
  ValueType,
>(
  registry: KeyedRegistry<
    { readonly id: string; readonly name: string } & Record<GetterKeyType, () => ValueType>
  > | null,
  entry: EntryType | null,
  getterKey: GetterKeyType,
  getValue: (entry: EntryType) => ValueType,
): void {
  const getter = useEffectEvent(() => (entry === null ? (undefined as ValueType) : getValue(entry)));
  const stable =
    entry === null
      ? null
      : ({ id: entry.id, name: entry.name, [getterKey]: getter } as {
          readonly id: string;
          readonly name: string;
        } & Record<GetterKeyType, () => ValueType>);
  useRegisterInRegistry(registry, stable);
}
